<script setup>
import {Link} from "@inertiajs/inertia-vue3";
import {computed, ref} from "vue";
import {ElSwitch} from "element-plus";
import {Inertia} from "@inertiajs/inertia";
import _ from 'lodash';
import {leadStillInCampaign} from "@/Components/partials/leadCampaignState.js";

const props = defineProps({
  lead: Object,
  leadCampaigns: {},
  leadCampaignsGlobalExclusion: {},
  leadOptedOutOfAgentEmails: {},
});

const campaignsEnabled = computed(() => ! Boolean(props.leadCampaignsGlobalExclusion));

const agentEmailsOptedOut = computed(() => Boolean(props.leadOptedOutOfAgentEmails));

const refetchOnly = ['leadCampaigns', 'leadCampaignsGlobalExclusion', 'leadOptedOutOfAgentEmails'];

const toggleAgentEmailOptOut = function(){
    if (agentEmailsOptedOut.value) {
        optInToAgentEmails();
    } else {
        optOutOfAgentEmails();
    }
};
const optOutOfAgentEmails = function(){
    Swal.fire({
        title: 'Are you sure?',
        text: 'This will opt this lead out of your marketing emails (campaigns, blasts, and autoresponders). You can still send them one-off emails.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, opt out of emails',
        cancelButtonText: 'No, cancel',
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.post(route('lead-campaigns.agent-email-opt-out', props.lead.id), {
                only: refetchOnly,
                onSuccess: () => {
                    $notify.success('Lead opted out of your marketing emails');
                },
            });
        }
    })
};
const optInToAgentEmails = function(){
    Swal.fire({
        title: 'Are you sure?',
        text: 'This will re-subscribe this lead to your marketing emails.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, re-subscribe',
        cancelButtonText: 'No, cancel',
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.post(route('lead-campaigns.agent-email-opt-in', props.lead.id), {
                only: refetchOnly,
                onSuccess: () => {
                    $notify.success('Lead re-subscribed to your marketing emails');
                },
            });
        }
    })
};

const toggleCampaignEnabled = function(){
    let currVal = campaignsEnabled.value;
    let newVal = !currVal;
    if (newVal) {
        enableCampaignsGlobally();
    } else {
        disableCampaignsGlobally();
    }
};
const enableCampaignsGlobally = function(){
    Swal.fire({
        title: 'Are you sure?',
        text: 'This will enable campaigns for this lead',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, enable campaigns',
        cancelButtonText: 'No, cancel',
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.post(route('lead-campaigns.global-enable', props.lead.id), {
                only: refetchOnly,
                onSuccess: () => {
                    $notify.success('Campaigns enabled for this lead');;
                },
            });
        }
    })
};
const disableCampaignsGlobally = function(){
    Swal.fire({
        title: 'Are you sure?',
        text: 'This will disable all campaigns for this lead',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, disable campaigns',
        cancelButtonText: 'No, cancel',
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.post(route('lead-campaigns.global-disable', props.lead.id), {
                only: refetchOnly,
                onSuccess: () => {
                    $notify.success('Campaigns disabled for this lead');
                },
            });
        }
    })
};

const getCampaignRoute = function(campaign){
    if (campaign.type === 'email') {
        return route('email-campaign.details.show', campaign.id);
    }
    if (campaign.type === 'text') {
        return route('text-campaign.details.show', campaign.id);
    }
};
const excludeFromCampaign = function(campaignId){
    Swal.fire({
        title: 'Are you sure?',
        text: 'This will remove this lead from the campaign',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove from campaign',
        cancelButtonText: 'No, cancel',
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.post(route('lead-campaigns.disable-lead-for-campaign', {lead: props.lead.id, campaign: campaignId}), {
                only: refetchOnly,
                onSuccess: () => {
                    $notify.success('Lead removed from campaign');
                },
            });
        }
    })
};
const disableExclusionFromCampaign = function(campaignId){
    Swal.fire({
        title: 'Are you sure?',
        text: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, stop excluding this lead',
        cancelButtonText: 'No',
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.post(route('lead-campaigns.enable-lead-for-campaign', {lead: props.lead.id, campaign: campaignId}), {
                only: refetchOnly,
                onSuccess: () => {
                    $notify.success('Exclusion removed');
                },
            });
        }
    })
};
const readdToCampaign = function(campaignId){
    Swal.fire({
        title: 'Are you sure?',
        text: 'This will re add this lead to the campaign',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, re add to campaign',
        cancelButtonText: 'No, cancel',
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.post(route('lead-campaigns.readd-lead-to-campaign', {lead: props.lead.id, campaign: campaignId}), {
                only: refetchOnly,
                onSuccess: () => {
                    $notify.success('Lead removed from campaign');
                },
            });
        }
    })
};
</script>

<template>
<div>
  <div class="flex items-center justify-start space-x-2 my-4">
    <el-switch :value="agentEmailsOptedOut" id="agent_emails_opted_out" @click.prevent="toggleAgentEmailOptOut" />
    <label class="cursor-pointer text-md" for="agent_emails_opted_out">Has this lead opted out of your marketing emails?</label>
  </div>
  <p v-if="agentEmailsOptedOut" class="text-sm text-red-500 -mt-2 mb-4">
    This lead unsubscribed from your marketing emails. They won't receive your campaigns, blasts, or autoresponders, but you can still send them one-off emails.
  </p>
  <div class="flex items-center justify-start space-x-2 my-4">
    <el-switch :value="campaignsEnabled" id="campaigns_enabled" @click.prevent="toggleCampaignEnabled" />
    <label class="cursor-pointer text-md" for="campaigns_enabled">Are campaigns enabled for this lead?</label>
  </div>
  <div v-show="campaignsEnabled" class="p-0 md:rounded-lg border ring-1 ring-black ring-opacity-5 overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-300">
      <thead class="bg-slate-200">
      <tr>
        <th scope="col" class="whitespace-nowrap px-3 py-3.5 text-sm text-left font-semibold text-gray-900">Name</th>
        <th scope="col" class="whitespace-nowrap px-3 py-3.5 text-sm text-left font-semibold text-gray-900">Type</th>
        <th scope="col" class="whitespace-nowrap px-3 py-3.5 text-sm text-left font-semibold text-gray-900">Status</th>
        <th scope="col" class="whitespace-nowrap px-3 py-3.5 text-sm text-left font-semibold text-gray-900">Is Ongoing</th>
        <th scope="col" class="whitespace-nowrap px-3 py-3.5 text-sm text-left font-semibold text-gray-900">Lead Joined At</th>
        <th scope="col" class="whitespace-nowrap px-3 py-3.5 text-sm text-left font-semibold text-gray-900">Lead Excluded</th>
        <th scope="col" class="whitespace-nowrap px-3 py-3.5 text-sm text-left font-semibold text-gray-900"></th>
      </tr>
      </thead>
      <tbody class="divide-y divide-gray-200 bg-white">
      <tr v-if="!leadCampaigns.length">
        <td class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900 text-center" colspan="100%">This lead is part of no campaigns</td>
      </tr>
      <tr v-else v-for="(leadCampaign, index) in leadCampaigns" :key="leadCampaign.id">
        <td class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900">{{ leadCampaign.name }}</td>
        <td class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900">{{ _.upperFirst(leadCampaign.type) }}</td>
        <td class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900">{{ _.upperFirst(leadCampaign.status) }}</td>
        <td class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900">{{ leadCampaign.ongoing ? 'Yes' : 'No' }}</td>
        <td class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900">{{ leadCampaign.joined_at }}</td>
        <td class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900" :class="{'text-red-500 font-bold': leadCampaign.lead_excluded}">
          {{ leadCampaign.lead_excluded ? 'Yes' : 'No' }}
        </td>
        <td class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900">
          <div class="flex space-x-4 flex-nowrap">
            <Link :href="getCampaignRoute(leadCampaign)" class="btn btn-primary btn-sm">View</Link>
            <button v-if="!leadCampaign.lead_excluded && leadStillInCampaign(leadCampaign)" class="btn text-sm btn-danger" @click="excludeFromCampaign(leadCampaign.id)">Remove</button>
            <button v-if="leadCampaign.lead_excluded && leadStillInCampaign(leadCampaign)" class="btn text-sm btn-primary" @click="disableExclusionFromCampaign(leadCampaign.id)">Remove Exclusion</button>
            <button v-if="campaignsEnabled && leadCampaign.ongoing && !leadCampaign.lead_excluded && _.upperFirst(leadCampaign.status) ==='Left'" class="btn text-sm btn-primary" @click="readdToCampaign(leadCampaign.id)">Add to campaign</button>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</div>
</template>